/**
 * Feature Flags Configuration
 *
 * Controls which features are enabled based on environment variables.
 * Used for development-only features like the admin section.
 */

/**
 * Check if giveaways feature is enabled
 * Controls giveaway pages, nav links, and related UI
 */
export const isGiveawaysEnabled = (): boolean => {
  return import.meta.env.VITE_FEATURE_GIVEAWAYS === 'true';
};

/**
 * Check if auth/login feature is enabled
 * Controls login button, auth routes, and user menu
 */
export const isAuthEnabled = (): boolean => {
  return import.meta.env.VITE_FEATURE_AUTH === 'true';
};

/**
 * Check if admin features are enabled
 * Admin is only available in development mode
 */
export const isAdminEnabled = (): boolean => {
  // Check environment variable (default to false in production)
  const envFlag = import.meta.env.VITE_ENABLE_ADMIN;

  // Admin is disabled in production
  if (import.meta.env.PROD) {
    return false;
  }

  // In development, check the flag (default to true for dev)
  return envFlag === 'true' || envFlag === true || envFlag === undefined;
};

/**
 * Require admin access - throws error if admin is not enabled
 * Use this in admin route components to ensure they're not accidentally loaded
 */
export const requireAdmin = (): void => {
  if (!isAdminEnabled()) {
    throw new Error(
      'Admin features are not enabled. ' +
        'Set VITE_ENABLE_ADMIN=true in .env file for local development.'
    );
  }
};

/**
 * Check if wireless thermometer mode is 'special'
 * @deprecated Thermometers are now just products. Always returns false.
 */
export const isSpecialMode = (): boolean => {
  return false;
};

/**
 * Get the current API base URL
 */
export const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_PUBLIC_API_URL || '';
};

/**
 * Check if we're in development mode
 */
export const isDevelopment = (): boolean => {
  return import.meta.env.DEV;
};

/**
 * Check if we're in production mode
 */
export const isProduction = (): boolean => {
  return import.meta.env.PROD;
};

/**
 * Get current app environment
 */
export const getAppEnv = (): string => {
  return import.meta.env.VITE_APP_ENV || (import.meta.env.PROD ? 'production' : 'development');
};
